import React, { RefObject, useCallback, useEffect, useRef, useState } from "react";
import intlTelInput from "intl-tel-input/intlTelInputWithUtils";
import { type Iti } from "intl-tel-input";
import { CountryInput } from "@sprout/country_input.tsx";
import { TextInput } from "@sprout/text_input.tsx";
import { Option } from "@sprout/select";
import "intl-tel-input/styles";

interface Props {
  id?: string;
  countryLabel: string;
  phoneLabel: string;
  maxLength?: number;
  required?: boolean;
  initialValue: string;
  help?: React.ReactNode | null;
  countryError: string | null;
  phoneError: string | null;
  onChange: (phone: string) => void;
  onError: (error: PhoneError | null) => void;
  countryRef: RefObject<HTMLDivElement | null>;
  phoneRef: RefObject<HTMLInputElement | null>;
}

export type PhoneError = "invalid" | "missing_country" | "too_short" | "too_long" | "unknown";

const convertCodeToError = (code: number | undefined): PhoneError => {
  switch (code) {
    case 0:
      return "invalid";
    case 1:
      return "missing_country";
    case 2:
      return "too_short";
    case 3:
      return "too_long";
    case 4:
      // This error actually means "local number", e.g. the number was provided
      // as 666-7777 instead of 555-666-7777
      return "too_short";
    default:
      return "unknown";
  }
};

// Encapsulates a country dropdown plus a text input for entering the phone number. We're using
// the intl-tel-input library to manage the text input, which handles auto-formatting
// and validation. We hide the library's country dropdown via CSS so that we can render our own
// country dropdown. We're doing that because the library's own country dropdown does not support
// having its own label, and relies on a default icon for its empty state, which isn't as clear
// to the user as a separate country dropdown with a label. The downside of our approach is it
// becomes a little more complex to manage the country state.
//
// This component has been modeled after intl-tel-input's own React component. We chose to make
// our own so that we can reuse TextInput and have more control over how things work.
// https://github.com/jackocnr/intl-tel-input/blob/master/react/src/intl-tel-input/reactWithUtils.tsx
export const PhoneInput = (props: Props) => {
  const {
    id,
    countryLabel,
    phoneLabel,
    maxLength,
    required = false,
    initialValue,
    help,
    countryError,
    phoneError,
    onChange,
    onError,
    countryRef,
    phoneRef,
  } = props;
  const [country, setCountry] = useState<Option | null>(null);
  const itiRef = useRef<Iti | null>(null);

  useEffect(() => {
    if (phoneRef.current) {
      itiRef.current = intlTelInput(phoneRef.current, {
        autoPlaceholder: "off",
        strictMode: true,
      });
    }

    return () => {
      itiRef.current?.destroy();
    };
  }, []);

  useEffect(() => {
    if (initialValue && itiRef.current && initialValue !== itiRef.current.getNumber()) {
      itiRef.current.setNumber(initialValue);
    }
  }, [initialValue]);

  const onPhoneChange = useCallback(() => {
    const number = itiRef.current?.getNumber() || "";
    onChange(number);

    if (itiRef.current?.isValidNumber()) {
      onError(null);
    } else {
      onError(convertCodeToError(itiRef.current?.getValidationError()));
    }
  }, [onChange, onError]);

  const onCountryChange = useCallback(
    (option: Option | null) => {
      setCountry(option);

      if (option) {
        itiRef.current?.setCountry(option.value.toString());
      }

      onPhoneChange();
      phoneRef.current?.focus();
    },
    [setCountry, onPhoneChange],
  );

  const syncCountryFromPlugin = useCallback(() => {
    const selectedCountryData = itiRef.current?.getSelectedCountryData() || {};
    const { iso2, name } = selectedCountryData;

    if (iso2 && name) {
      setCountry({ value: iso2, label: name });
    } else {
      setCountry(null);
    }
  }, [setCountry]);

  useEffect(() => {
    // Store a reference to the current phone ref, otherwise it will be null in the cleanup function
    const phoneRefCurrent = phoneRef.current;

    // The plugin can detect a country change in a few scenarios:
    //  a) The user typed an explicit dial code before entering their number, like "+44"
    //  b) The user selected the US but then entered a Canadian area code, etc.
    //  c) Triggered via iti.setCountry() above, but basically a no-op in this case
    phoneRefCurrent?.addEventListener("countrychange", syncCountryFromPlugin);

    // If there is an initial value on page load, the plugin automatically determines the country
    // from the phone number. We need to sync that back to our own country select. We also want to
    // ensure the onError callback is called via onPhoneChange, e.g. if the initial value predates
    // this component and does not have a dial code.
    syncCountryFromPlugin();
    onPhoneChange();

    return () => {
      phoneRefCurrent?.removeEventListener("countrychange", syncCountryFromPlugin);
    };
  }, [syncCountryFromPlugin, onPhoneChange]);

  return (
    <fieldset className="phone-input">
      <legend className="sr-only">{phoneLabel}</legend>

      <div className="phone-input__country">
        <CountryInput
          ref={countryRef}
          onChange={onCountryChange}
          value={country}
          label={countryLabel}
          error={countryError}
        />
      </div>

      <div className="phone-input__phone">
        <TextInput
          ref={phoneRef}
          id={id}
          type="tel"
          label={phoneLabel}
          required={required}
          help={help}
          error={phoneError}
          defaultValue={initialValue}
          maxLength={maxLength}
          onInput={onPhoneChange}
        />
      </div>
    </fieldset>
  );
};
